/**
 * Frecency scoring for command palette results.
 *
 * Tracks which items users select and ranks by frequency * recency.
 * Adapted from Firefox's URL bar frecency algorithm.
 *
 * Item IDs follow the format: "type::identifier"
 *   patient::uuid, nav::id, note::uuid, action::id
 *
 * Storage: sessionStorage (not localStorage). Frecency meta contains patient names
 * and DOBs — on a shared workstation this was readable by the next user (SEC-026).
 * sessionStorage is per-tab and dies when the tab closes, which matches the threat
 * model for shared devices. Scores still persist within a session.
 */

const STORAGE_KEY = 'claro_frecency'
const OLD_RECENT_KEY = 'claro_recent_patients'
const MAX_ENTRIES = 200
const VERSION = 1

// One-time cleanup of the legacy localStorage data (written before SEC-026 fix).
// Runs on module import so old name+DOB entries don't linger after the user upgrades.
try {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(OLD_RECENT_KEY)
  }
} catch { /* ignore — SSR or storage disabled */ }

function storage(): Storage | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null
  } catch {
    return null
  }
}

export interface FrecencyMeta {
  label: string
  subtitle?: string
  dob?: string
  icon?: string
}

interface FrecencyEntry {
  id: string
  count: number
  lastAccess: number
  meta?: FrecencyMeta
}

interface FrecencyStore {
  version: number
  entries: Record<string, FrecencyEntry>
}

// --- Scoring ---

function recencyWeight(hoursSince: number): number {
  if (hoursSince < 4) return 100
  if (hoursSince < 24) return 70
  if (hoursSince < 72) return 50
  if (hoursSince < 336) return 30  // 2 weeks
  if (hoursSince < 720) return 10  // 30 days
  return 0
}

export function frecencyScore(entry: FrecencyEntry, now = Date.now()): number {
  const hoursSince = (now - entry.lastAccess) / 3_600_000
  return entry.count * recencyWeight(hoursSince)
}

// --- Storage ---

function load(): FrecencyStore {
  const s = storage()
  if (!s) return { version: VERSION, entries: {} }
  try {
    const raw = s.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as FrecencyStore
      if (parsed.version === VERSION) return parsed
    }
  } catch { /* ignore corrupt data */ }

  // Fresh session: empty store. Old localStorage-based recents are deliberately
  // NOT migrated to sessionStorage — migrating would re-expose the PHI on shared
  // devices. The legacy data is wiped at module load (see top of file).
  const store: FrecencyStore = { version: VERSION, entries: {} }
  save(store)
  return store
}

function save(store: FrecencyStore): void {
  const s = storage()
  if (!s) return
  try {
    s.setItem(STORAGE_KEY, JSON.stringify(store))
  } catch { /* storage full — silently ignore */ }
}

// --- Public API ---

/**
 * Record a selection in the command palette.
 * Call this every time the user selects a result.
 */
export function recordSelection(id: string, meta?: FrecencyMeta): void {
  const store = load()
  const existing = store.entries[id]

  store.entries[id] = {
    id,
    count: (existing?.count ?? 0) + 1,
    lastAccess: Date.now(),
    meta: meta ?? existing?.meta,
  }

  // Prune if over limit
  const entries = Object.values(store.entries)
  if (entries.length > MAX_ENTRIES) {
    prune(store)
  }

  save(store)
}

/**
 * Get top entries by frecency score, optionally filtered by ID prefix.
 */
export function getTopEntries(limit: number, prefix?: string): FrecencyEntry[] {
  const store = load()
  const now = Date.now()

  let entries = Object.values(store.entries)

  if (prefix) {
    entries = entries.filter((e) => e.id.startsWith(prefix))
  }

  return entries
    .map((e) => ({ ...e, _score: frecencyScore(e, now) }))
    .filter((e) => e._score > 0)
    .sort((a, b) => b._score - a._score)
    .slice(0, limit)
}

/**
 * Get the frecency score for a single item. Returns 0 if not tracked.
 */
export function getFrecencyScore(id: string): number {
  const store = load()
  const entry = store.entries[id]
  if (!entry) return 0
  return frecencyScore(entry)
}

/**
 * Sort an array by frecency, preserving original order for unscored items.
 * Returns a new array.
 */
export function sortByFrecency<T>(items: T[], getId: (item: T) => string): T[] {
  const store = load()
  const now = Date.now()

  return [...items].sort((a, b) => {
    const entryA = store.entries[getId(a)]
    const entryB = store.entries[getId(b)]
    const scoreA = entryA ? frecencyScore(entryA, now) : 0
    const scoreB = entryB ? frecencyScore(entryB, now) : 0

    // Both unscored → preserve original order
    if (scoreA === 0 && scoreB === 0) return 0
    return scoreB - scoreA
  })
}

/**
 * Remove stale entries (score 0) and trim to max size.
 */
function prune(store: FrecencyStore): void {
  const now = Date.now()
  const entries = Object.values(store.entries)
    .map((e) => ({ ...e, _score: frecencyScore(e, now) }))
    .filter((e) => e._score > 0)
    .sort((a, b) => b._score - a._score)
    .slice(0, MAX_ENTRIES)

  store.entries = {}
  for (const e of entries) {
    store.entries[e.id] = { id: e.id, count: e.count, lastAccess: e.lastAccess, meta: e.meta }
  }
}

/**
 * Clear all frecency data. Called on logout.
 *
 * Clears both sessionStorage (current home) and localStorage (legacy location, for
 * users who still have old data from before the SEC-026 migration).
 */
export function clearFrecency(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch { /* ignore */ }
  try {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(OLD_RECENT_KEY)
  } catch { /* ignore */ }
}
